import Papa from "papaparse";

import { ResultFormat } from "src/gen/schema/bilge/api";

export const MAX_OUTPUT_LINES = 1000;
export interface CommandOutput {
  readonly id: string;
  readonly command: string;
  readonly output: string;
  readonly resultFormat: ResultFormat;
  readonly executedAt: Date;
  readonly lineCount: number;
  readonly deviceId?: string;
  readonly emptyStateText?: string;
}

export interface CommandHistory {
  readonly commands: CommandOutput[];
  readonly totalLines: number;
}

// Create initial empty history
export const createInitialHistory = (): CommandHistory => ({
  commands: [],
  totalLines: 0
});

// Count lines in output string
const countLines = (output: string, resultFormat: ResultFormat): number => {
  if (resultFormat === ResultFormat.CSV) {
    // For CSV, count actual data rows (excluding header)
    const result = Papa.parse(output, {
      header: true,
      skipEmptyLines: true
    });
    // Return row count + 1 for header
    return result.data.length + 1;
  }
  // For TEXT format, count newlines
  return output.split("\n").length;
};

// Generate unique ID
const generateId = (): string => {
  return `cmd-${crypto.randomUUID()}`;
};

/**
 * Trim output to a specified number of lines.
 * For TEXT: keeps the LAST N lines (like terminal scrollback — most recent at bottom).
 * For CSV: keeps header row + last (N-1) data rows (so table remains readable).
 *
 * @param output - Raw output string
 * @param resultFormat - TEXT or CSV
 * @param maxLines - Number of lines to keep
 * @returns Trimmed output string
 */
const trimOutputToLineCount = (
  output: string,
  resultFormat: ResultFormat,
  maxLines: number
): string => {
  if (maxLines <= 0) return "";

  if (resultFormat === ResultFormat.CSV) {
    const result = Papa.parse(output, {
      header: false,
      skipEmptyLines: true
    });
    const rows = result.data as string[][];
    if (rows.length <= maxLines) return output;

    // Keep header (first row) + last (maxLines - 1) data rows
    const headerRow = rows[0];
    const dataRows = maxLines > 1 ? rows.slice(-(maxLines - 1)) : [];
    return Papa.unparse([headerRow, ...dataRows]);
  }

  // TEXT format: keep last N lines (terminal scrollback behavior)
  const lines = output.split("\n");
  if (lines.length <= maxLines) return output;
  return lines.slice(-maxLines).join("\n");
};

/**
 * Trim history to maintain exactly 1000 total output lines.
 * Rules:
 * - Current command (index 0): Always shown completely, even if > 1000 lines
 * - Previous commands: Loop through all, include complete ones that fit,
 *   then partially trim the last one to reach exactly 1000 lines
 * - Handles both TEXT and CSV (table) formats
 *
 * @param history - Command history with newest command at index 0
 * @returns Trimmed history with totalLines capped at MAX_OUTPUT_LINES
 */
const trimToLineLimit = (history: CommandHistory): CommandHistory => {
  const { commands } = history;
  const currentCommand = commands[0];
  if (!currentCommand) return history;

  // Always keep current command completely, even if > 1000 lines
  if (currentCommand.lineCount >= MAX_OUTPUT_LINES) {
    return {
      commands: [currentCommand],
      totalLines: currentCommand.lineCount
    };
  }

  let totalLines = currentCommand.lineCount;
  const trimmedCommands: CommandOutput[] = [currentCommand];

  // Loop through all previous commands
  for (let i = 1; i < commands.length; i++) {
    const cmd = commands[i];
    if (cmd) {
      const remainingLines = MAX_OUTPUT_LINES - totalLines;

      if (remainingLines <= 0) break;

      if (cmd.lineCount <= remainingLines) {
        // Previous command fits completely — include it
        trimmedCommands.push(cmd);
        totalLines += cmd.lineCount;
      } else {
        // Previous command doesn't fully fit — trim to fill remaining lines exactly
        const trimmedOutput = trimOutputToLineCount(
          cmd.output,
          cmd.resultFormat,
          remainingLines
        );
        trimmedCommands.push({
          ...cmd,
          output: trimmedOutput,
          lineCount: remainingLines
        });
        totalLines = MAX_OUTPUT_LINES;
        break; // We've reached exactly 1000 lines
      }
    }
  }

  return {
    commands: trimmedCommands,
    totalLines
  };
};

// Add new command output to history (prepends - latest first)
export const addCommandOutput = (
  history: CommandHistory | undefined,
  command: string,
  output: string,
  resultFormat: ResultFormat,
  executedAt?: Date,
  deviceId?: string,
  emptyStateText?: string
): CommandHistory => {
  const currentHistory = history ?? createInitialHistory();
  const lineCount = countLines(output, resultFormat);

  const newCommand: CommandOutput = {
    id: generateId(),
    command,
    output,
    resultFormat,
    executedAt: executedAt ?? new Date(),
    lineCount,
    deviceId,
    emptyStateText
  };

  const newHistory: CommandHistory = {
    commands: [newCommand, ...currentHistory.commands],
    totalLines: currentHistory.totalLines + lineCount
  };

  return trimToLineLimit(newHistory);
};
